import React from 'react'
import { CircularProgress } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBookmark,
  faCheck,
  faCircleCheck,
  faClock,
  faCopy,
  faFileLines,
} from '@fortawesome/pro-regular-svg-icons'
import { faBookmark as faBookmarkSolid } from '@fortawesome/pro-solid-svg-icons'
import { LabraToolTipV2 } from '@labrav/react-components'
import {
  useLabraChatContext,
  useLabraChatDispatch,
  useLabraChatSelector,
} from '../../../modules/labraChat/context'
import {
  deleteLabraChatSavedPrompt,
  openLabraChatPreview,
  saveLabraChatPrompt,
} from '../../../modules/labraChat/actions'
import {
  selectLabraChatSavedPrompts,
  selectLabraChatSessionId,
} from '../../../modules/labraChat/selectors'
import { useTrackEvent } from '../../../telemetry/analytics'
import { LabraChatMessage } from '../../../modules/labraChat/reducer'
import { flyoutAssetUrlFromS3Uri } from '../../../modules/labraChat/api'
import { copyToClipboard } from '../../../utils/copyToClipboard'
import { UserProfile } from '../../../modules/userProfile/action'
import { useStyles } from './UserBubble.styles'
import { formatMessageTime } from './ChatThread.helpers'
import { useUserDisplayTimeZone } from './useUserDisplayTimeZone'

interface UserBubbleProps {
  message: LabraChatMessage
}

/** How long the copy button shows its confirmation tick. */
const COPY_FEEDBACK_MS = 2000

const UserBubbleComponent: React.FC<UserBubbleProps> = ({ message }) => {
  const { classes, cx } = useStyles()
  const dispatch = useLabraChatDispatch()
  const { partnerId } = useLabraChatContext()
  const trackEvent = useTrackEvent()
  const sessionId = useLabraChatSelector(selectLabraChatSessionId)
  const savedPrompts = useLabraChatSelector(selectLabraChatSavedPrompts)

  const [isSaving, setIsSaving] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  const user = useLabraChatSelector<UserProfile | undefined>(
    state => state.userProfile?.userProfile
  )
  const userTimezone = useUserDisplayTimeZone()

  const initials =
    [user?.firstName?.charAt(0) ?? '', user?.lastName?.charAt(0) ?? '']
      .join('')
      .toUpperCase() || 'U'

  const documents = message.documents ?? []
  const time = formatMessageTime(message.timestamp, userTimezone)
  const savedPrompt = savedPrompts.find(
    ({ prompt }) => prompt === message.content
  )

  // The bookmark toggles: a second click takes the prompt back out of the list.
  const handleToggleSave = async () => {
    if (savedPrompt) {
      dispatch(deleteLabraChatSavedPrompt(partnerId, savedPrompt.id))
      return
    }

    setIsSaving(true)
    const result = await dispatch(
      saveLabraChatPrompt(partnerId, message.content)
    )
    setIsSaving(false)

    if (result === 'created') {
      trackEvent({
        name: 'labra_ai_chat_prompt_saved',
        conversation_id: sessionId,
      })
    }
  }

  const handleCopy = () => {
    copyToClipboard(message.content)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), COPY_FEEDBACK_MS)
      })
      .catch(() => undefined)
  }

  return (
    <div className={classes.row}>
      <div className={classes.column}>
        {message.content && (
          <div className={classes.bubble}>{message.content}</div>
        )}
        {documents.length > 0 && (
          <div className={classes.attachments}>
            {documents.map(doc => {
              const href = flyoutAssetUrlFromS3Uri(doc.s3_uri)
              const inner = (
                <>
                  <FontAwesomeIcon icon={faFileLines} />
                  <span className={classes.pillName}>{doc.filename}</span>
                </>
              )
              return href ? (
                <a
                  key={doc.s3_uri}
                  className={classes.pill}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={doc.filename}
                  onClick={event => {
                    // Let the browser handle the modified clicks that mean
                    // "open elsewhere"; only plain clicks open the preview.
                    if (event.metaKey || event.ctrlKey || event.shiftKey) return
                    event.preventDefault()
                    dispatch(openLabraChatPreview(doc))
                  }}
                >
                  {inner}
                </a>
              ) : (
                <span
                  key={doc.s3_uri}
                  className={classes.pill}
                  title={doc.filename}
                >
                  {inner}
                </span>
              )
            })}
          </div>
        )}
        <div className={classes.actionRow}>
          {/* An attachment-only turn has no text, and the API rejects an empty
              prompt — so it gets the timestamp alone. */}
          {message.content && (
            <>
              <LabraToolTipV2
                tooltipPlacement="top"
                // TODO(copy): "Remove saved prompt" is not in Figma — confirm.
                description={
                  savedPrompt ? 'Remove saved prompt' : 'Save prompt'
                }
                icon={
                  savedPrompt ? (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className={classes.confirmedIcon}
                    />
                  ) : undefined
                }
              >
                <button
                  className={classes.actionButton}
                  type="button"
                  onClick={handleToggleSave}
                  disabled={isSaving}
                  aria-label={
                    savedPrompt ? 'Remove saved prompt' : 'Save prompt'
                  }
                >
                  {isSaving ? (
                    <CircularProgress size={12} thickness={5} />
                  ) : (
                    <FontAwesomeIcon
                      icon={savedPrompt ? faBookmarkSolid : faBookmark}
                    />
                  )}
                </button>
              </LabraToolTipV2>
              <LabraToolTipV2
                tooltipPlacement="top"
                description={copied ? 'Copied!' : 'Copy'}
              >
                <button
                  className={classes.actionButton}
                  type="button"
                  onClick={handleCopy}
                  aria-label="Copy"
                >
                  <FontAwesomeIcon
                    icon={copied ? faCheck : faCopy}
                    className={cx(copied && classes.confirmedIcon)}
                  />
                </button>
              </LabraToolTipV2>
              {time && <span className={classes.actionDivider} />}
            </>
          )}
          {time && (
            <span className={classes.time}>
              <FontAwesomeIcon icon={faClock} />
              {time}
            </span>
          )}
        </div>
      </div>
      <div className={classes.avatar}>{initials}</div>
    </div>
  )
}

// Memoized so streaming updates to the assistant bubble don't re-render
// every user bubble in the thread.
export const UserBubble = React.memo(UserBubbleComponent)
